import { useContextI18nState, useContextI18nDispatch } from 'core/context/i18n';
import { setData } from 'core/context/actions';

/**
 * Custom hook for internacionalization.
 * useTranslation
 * @category Custom Hook
 * @id i18n
 * @return {Object} Provides 2 functions: {t as translate, changeLang}
 * @see translate
 * @see changeLang
 */
export default function useTranslation() {
  const contextI18nState = useContextI18nState();
  const contextI18nDispatch = useContextI18nDispatch();

  /**
   * Update current Lang of I18N Context.
   * changeLang
   * @category Function
   * @id i18n
   * @return {Void}
   */
  function changeLang(lang: string) {
    if (!contextI18nDispatch) throw new Error('Context is not provided');
    const { availableLanguages } = contextI18nState;
    if (availableLanguages.indexOf(lang) < 0) lang = availableLanguages[0];
    contextI18nDispatch(setData({ currentLang: lang }));
  }

  /**
   * Update current Lang of I18N Context.
   * changeLang
   * @category Function
   * @id i18n
   * @function translate
   * @param  {String} key - key to look up in the dictionary
   * @param  {String} forcedLang - (optional) one of the available languages to show a specific value to that language
   * @return {String} result of translation or initial key
   */
  function translate(key: string, forcedLang?: string) {
    if (!contextI18nState) throw new Error('Context is not provided');
    if (!key) throw new Error('Key is not provided');

    const { dictionary, currentLang } = contextI18nState;
    const lang = forcedLang || currentLang;
    let aux = dictionary?.[lang];
    const splitted: string[] = key.split('.');
    for (const subkey of splitted) {
      if (typeof aux !== 'undefined') {
        aux = aux[subkey];
      } else {
        break;
      }
    }
    return aux || key;
  }

  return {
    t: translate,
    changeLang: changeLang,
  };
}
